import React, { useCallback, useState } from "react";
import styled from "styled-components";
import { Card as BootstrapCard } from "react-bootstrap";
import { defineMessages, FormattedMessage } from "react-intl";
import axios from "http/customAxios";
import { useConfig } from "../../lib/config";

import { FullWidthLoader } from "../../components/full-width-loader";
import { SectionHeading } from "../../components/section-heading";

import { defaultFormMessages } from "../shared/crud/form";
import { defaultListMessages } from "../shared/crud/list";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import { useIntl } from "react-intl";

const messages = {
  ...defaultFormMessages,
  ...defaultListMessages,
  ...defineMessages({
    title: {
      id: "internalmenu.integrations.verifyUserLinkedProperties",
      defaultMessage: "Verify Users Linked Properties Manager",
    },
    userEmailPlaceholder: {
      id: "userPropertiesManager.placeholder.userEmail",
      defaultMessage: "Enter user email",
    },
    userEmailLabel: {
      id: "userPropertiesManager.label.userEmail",
      defaultMessage: "User Email",
    },
    listOfProperties: {
      id: "userPropertiesManager.listOfProperties",
      defaultMessage: "Properties List",
    },
    propertyTicker: {
      id: "userPropertiesManager.propertyTicker",
      defaultMessage: "Property Ticker",
    },
    searchButton: {
      id: "userPropertiesManager.searchButton",
      defaultMessage: "Search Properties",
    },
    noPropertiesFound: {
      id: "userPropertiesManager.noPropertiesFound",
      defaultMessage: "No properties found for this user",
    },
    invalidEmailFormat: {
      id: "userPropertiesManager.invalidEmailFormat",
      defaultMessage: "Please enter a valid email format",
    },
    queryError: {
      id: "userPropertiesManager.queryError",
      defaultMessage: "Error executing query",
    },
    unknownError: {
      id: "userPropertiesManager.unknownError",
      defaultMessage: "Unknown error",
    },
  }),
};

interface EmailInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  hasError?: boolean;
}

const StyledCard = styled(BootstrapCard)`
  margin: 1rem;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin: 1rem;
  > * {
    margin: 1rem 0;
  }
`;

const Label = styled.div`
  font-size: 18px;
  margin-bottom: 10px;
`;

const EmailInput = styled.input<EmailInputProps>`
  padding: 10px;
  border: 1px solid ${props => (props.hasError ? "#dc3545" : "#ccc")};
  border-radius: 4px;
  font-size: 16px;
  width: 100%;
  max-width: 400px;

  &:focus {
    outline: none;
    border-color: ${props => (props.hasError ? "#dc3545" : "#007bff")};
    box-shadow: 0 0 0 0.2rem
      ${props => (props.hasError ? "rgba(220, 53, 69, 0.25)" : "rgba(0, 123, 255, 0.25)")};
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 14px;
  margin-top: 5px;
  margin-bottom: 10px;
`;

const SearchButton = styled.button`
  padding: 10px 20px;
  background-color: #ff5121;
  color: white;
  border: none;
  border-radius: 24px;
  cursor: pointer;
  font-size: 16px;
  margin-top: 10px;
  width: 400px;

  &:hover {
    background-color: #ff5121;
  }

  &:disabled {
    background-color: #6c757d;
    cursor: not-allowed;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;

  th,
  td {
    padding: 12px;
    text-align: left;
    border-bottom: 1px solid #ddd;
  }

  th {
    background-color: #f8f9fa;
    font-weight: bold;
  }

  tr:hover {
    background-color: #f5f5f5;
  }
`;

const NoDataMessage = styled.div`
  text-align: center;
  padding: 20px;
  color: #6c757d;
  font-style: italic;
`;

const ErrorQueryMessage = styled.div`
  background-color: #f8d7da;
  color: #721c24;
  padding: 12px;
  border: 1px solid #f5c6cb;
  border-radius: 4px;
  margin: 10px 0;
`;

function UserPropertiesManager() {
  const { queryExecutorApiEndpoint } = useConfig();

  const [loading, setLoading] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [userProperties, setUserProperties] = useState<string[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [queryError, setQueryError] = useState("");

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const intl = useIntl();

  const executeUserPropertiesQuery = useCallback(
    async (username: string) => {
      const url = `${queryExecutorApiEndpoint}/query/${encodeURIComponent(username)}/properties`;
      const response = await axios.get(url);
      if (Array.isArray(response.data)) {
        return response.data;
      }
      return [];
    },
    [queryExecutorApiEndpoint]
  );

  const handleEmailChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const email = e.target.value;
      setUserEmail(email);

      if (!email.trim()) {
        setEmailError("");
        return;
      }

      if (!validateEmail(email)) {
        setEmailError(intl.formatMessage(messages.invalidEmailFormat));
      } else {
        setEmailError("");
      }
    },
    [intl]
  );

  const handleSearchProperties = useCallback(async () => {
    if (!userEmail.trim()) {
      return;
    }
    if (!validateEmail(userEmail.trim())) {
      setEmailError(messages.invalidEmailFormat.defaultMessage);
      return;
    }

    setLoading(true);
    setHasSearched(true);
    setQueryError("");

    try {
      const properties = await executeUserPropertiesQuery(userEmail.trim());
      setUserProperties(properties);
    } catch (error) {
      console.error("Error searching for user properties:", error);
      setUserProperties([]);
      setQueryError(error instanceof Error ? error.message : messages.queryError.defaultMessage);
    } finally {
      setLoading(false);
    }
  }, [userEmail, executeUserPropertiesQuery]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        handleSearchProperties();
      }
    },
    [handleSearchProperties]
  );

  if (loading) {
    return <FullWidthLoader />;
  }

  return (
    <>
      <SectionHeading>
        <FormattedMessage {...messages.title} />
      </SectionHeading>
      <StyledCard>
        <Container>
          <Label>
            <FormattedMessage {...messages.userEmailLabel} />
          </Label>
          <EmailInput
            type="email"
            value={userEmail}
            onChange={handleEmailChange}
            onKeyDown={handleKeyDown}
            placeholder={intl.formatMessage(messages.userEmailPlaceholder)}
            hasError={!!emailError}
          />
          {emailError && <ErrorMessage>{emailError}</ErrorMessage>}
          <SearchButton
            onClick={handleSearchProperties}
            disabled={!userEmail.trim() || loading || !!emailError}>
            <FormattedMessage {...messages.searchButton} />
          </SearchButton>
        </Container>

        {hasSearched && (
          <Container>
            <Label>
              <FormattedMessage {...messages.listOfProperties} />
            </Label>
            {queryError && <ErrorQueryMessage>{queryError}</ErrorQueryMessage>}
            {!queryError && userProperties.length > 0 ? (
              <Table>
                <thead>
                  <tr>
                    <th>
                      <FormattedMessage {...messages.propertyTicker} />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {userProperties.map((propertyTicker, index) => (
                    <tr key={index}>
                      <td>{propertyTicker}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              !queryError && (
                <NoDataMessage>
                  <FormattedMessage {...messages.noPropertiesFound} />
                </NoDataMessage>
              )
            )}
          </Container>
        )}
      </StyledCard>
    </>
  );
}

export default withAuthorizationVerifier(UserPropertiesManager);
